/**
 * What the Environment form shows before saving: the key names in a pasted env
 * file and the lines that are not `KEY=value`. A copy of core's `parseEnv`
 * (`core/src/lib/env-file.ts`) reduced to what the preview needs; the test checks
 * the two agree. Never returns a value.
 */

const KEY_LINE = /^\s*(?:export\s+)?([A-Za-z_][A-Za-z0-9_.-]*)\s*=(.*)$/;
/** A key name as `KEY_LINE` accepts one. */
export const ENV_KEY_NAME = /^[A-Za-z_][A-Za-z0-9_.-]*$/;
const IGNORABLE = /^\s*(#.*)?$/;

/** Mirrors core's `ENV_FILENAME`, minus the committed templates. */
export const ENV_FILENAME = /^\.env(\.[a-z0-9_-]+)?$/;
const TEMPLATES = new Set(["example", "sample", "template", "dist", "defaults"]);
export const isWritableEnvName = (name: string): boolean => {
  const match = ENV_FILENAME.exec(name);
  return match !== null && !TEMPLATES.has((match[1] ?? "").slice(1));
};

export const MAX_ENV_BYTES = 64 * 1024;

function unclosedQuote(rawValue: string, from = 1): '"' | "'" | null {
  const value = rawValue.trimStart();
  const quote = value[0];
  if (quote !== '"' && quote !== "'") return null;
  return closes(value.slice(from), quote) ? null : quote;
}

function closes(text: string, quote: '"' | "'"): boolean {
  for (let i = 0; i < text.length; i++) {
    if (quote === '"' && text[i] === "\\") {
      i++;
      continue;
    }
    if (text[i] === quote) return true;
  }
  return false;
}

export interface EnvPreview {
  keys: string[];
  invalidLines: number[];
}

export function previewEnv(text: string): EnvPreview {
  const lines = text.split(/\r?\n/);
  if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  const keys = new Set<string>();
  const invalidLines: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    const match = KEY_LINE.exec(lines[i]!);
    if (!match) {
      if (!IGNORABLE.test(lines[i]!)) invalidLines.push(i + 1);
      continue;
    }
    keys.add(match[1]!);
    const quote = unclosedQuote(match[2]!);
    if (quote) {
      while (i + 1 < lines.length) {
        if (closes(lines[++i]!, quote)) break;
      }
    }
  }
  return { keys: [...keys], invalidLines };
}
